/**
 * Wrapper routes for the existing per-feature components.
 *
 * The legacy components (CycleDashboard, SubmitPage,
 * CapacityPage, etc.) read the active cycle id from
 * `?cycle=<uuid>` or localStorage's `roster.dev_cycle_id`. The Phase
 * 14 router introduces canonical paths like `/cycles/:cycleId`; this
 * file maps from those paths into localStorage *before* the legacy
 * component mounts, so the existing fetch logic Just Works without
 * needing to gut tested components.
 */
import { Suspense, lazy, useLayoutEffect } from "react";
import { Navigate, useParams } from "react-router-dom";

import { useBreadcrumbs } from "../components/Breadcrumbs";
import { Skeleton } from "../components/ui";
import { useCycleCrumbs } from "../lib/breadcrumbCrumbs";

const CycleDashboard = lazy(() => import("../components/CycleDashboard"));
const SubmitPage = lazy(() => import("../components/SubmitPage"));
const CapacityPage = lazy(() => import("../components/CapacityPage"));
const BlocksEditor = lazy(() => import("../components/BlocksEditor"));
const CyclePage = lazy(() => import("./CyclePage").then((m) => ({ default: m.CyclePage })));

const CYCLE_KEY = "roster.dev_cycle_id";

function PageSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-10 w-2/3" />
      <Skeleton className="h-32" />
      <Skeleton className="h-64" />
    </div>
  );
}

function useStashCycleId(cycleId: string | undefined) {
  useLayoutEffect(() => {
    if (cycleId && typeof window !== "undefined") {
      window.localStorage.setItem(CYCLE_KEY, cycleId);
    }
  }, [cycleId]);
}

/**
 * Wave-8A scaffold (#504) — wrapper for the cycle workspace shell.
 * Mounted on `/cycles/:cycleId` directly + on the legacy sub-route
 * paths (`/cycles/:cycleId/{roster,submissions,solve,demand,...}`)
 * for backward-compat. The shell is sub-route-free; old segments are
 * parsed as `SurfaceHint` inside `<CyclePage/>` so deep links and
 * `?tab=X` query params still resolve (wave-8B will use the hint to
 * scroll to the relevant in-page surface).
 */
export function CyclePageRoute() {
  const { cycleId } = useParams<{ cycleId: string }>();
  useStashCycleId(cycleId);
  // Issue #122 — `useCycleCrumbs` resolves `[Home, <team>, <cycle>]`.
  // The inner `<CyclePage/>` re-registers the same chain once its
  // own cycle query resolves; both paths share the query cache so
  // the wrapper-level fetch is free.
  useBreadcrumbs(useCycleCrumbs(cycleId));
  return (
    <Suspense fallback={<PageSkeleton />}>
      <CyclePage />
    </Suspense>
  );
}

/**
 * Bare `/cycles/:cycleId` entry — wave-8A: renders the workspace shell
 * directly (no sub-route redirect any more; the shell is one page).
 * Pre-8A this used to redirect to `/cycles/:cycleId/roster`; with tabs
 * gone, that hop is dead weight. Kept exported under the legacy name
 * so `App.tsx`'s route table doesn't have to refactor.
 */
export function CycleIndexRoute() {
  const { cycleId } = useParams<{ cycleId: string }>();
  if (!cycleId) {
    return <Navigate to="/" replace />;
  }
  return <CyclePageRoute />;
}

/**
 * IA Phase 1a — `/solve` legacy redirect. Solve is now a per-cycle
 * internal tab. If localStorage carries an active cycle id (from a
 * prior in-app navigation), redirect to `/cycles/<id>/solve`.
 * Otherwise bounce to home so the lead picks a team → cycle.
 */
export function LegacySolveRedirect() {
  const stored =
    typeof window !== "undefined" ? window.localStorage.getItem("roster.dev_cycle_id") : null;
  if (stored) {
    return <Navigate to={`/cycles/${stored}/solve`} replace />;
  }
  return <Navigate to="/" replace />;
}

export function LegacyCycleDashboardRoute() {
  // Path-only legacy alias `/cycle` — reads `?cycle=<id>` or localStorage.
  useBreadcrumbs([{ label: "Home", to: "/" }, { label: "Roster" }]);
  return (
    <Suspense fallback={<PageSkeleton />}>
      <CycleDashboard />
    </Suspense>
  );
}

export function SubmitRoute() {
  const { cycleId } = useParams<{ cycleId: string }>();
  useStashCycleId(cycleId);
  // Issue #122 — when the URL is `/cycles/<id>/submit`, prepend
  // `[Home, <team>, <cycle>]` so the lead can step back one level
  // (cycle dashboard) instead of jumping all the way home. The
  // legacy bare `/submit` alias has no `cycleId` in the URL, so
  // `useCycleCrumbs` falls back to `[Home]` and we keep the
  // existing single-leaf shape.
  const cycleCrumbs = useCycleCrumbs(cycleId);
  useBreadcrumbs([...cycleCrumbs, { label: "Submit availability" }]);
  return (
    <Suspense fallback={<PageSkeleton />}>
      <SubmitPage />
    </Suspense>
  );
}

export function CapacityRoute() {
  const { cycleId } = useParams<{ cycleId: string }>();
  useStashCycleId(cycleId);
  useBreadcrumbs([{ label: "Home", to: "/" }, { label: "Capacity" }]);
  return (
    <Suspense fallback={<PageSkeleton />}>
      <CapacityPage />
    </Suspense>
  );
}

export function BlocksEditorRoute() {
  const { cycleId } = useParams<{ cycleId: string }>();
  useStashCycleId(cycleId);
  // Issue #122 — `[Home, <team>, <cycle>, "Edit leaves & blocks"]`.
  // The team segment was previously absent so clicking the only
  // clickable crumb (Home) jumped past both team and cycle.
  const cycleCrumbs = useCycleCrumbs(cycleId);
  useBreadcrumbs([...cycleCrumbs, { label: "Edit leaves & blocks" }]);
  return (
    <Suspense fallback={<PageSkeleton />}>
      <BlocksEditor />
    </Suspense>
  );
}
